import React from "react";
import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { ExternalLink } from "../../common/components/ExternalLink";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { SurveyDefinition as SurveyDefinition } from "./survey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataMapDto } from "../../common/service/server/dto/datamap/DataMapDto";
import {
  findGoogleAnalytics,
  findShopifyAudiences,
  findAlwaysSellingVendors,
} from "../util/vendors";

export const BUSINESS_SURVEY_NAME = "business-survey";

export const COLLECTION_FOR_OTHER_BUSINESSES_SLUG = "consumer-collection-for-other-businesses";
export const CONNECTICUT_PHYSICAL_LOCATION_SLUG = "business-connecticut-physical-location";
export const CONNECTICUT_FACIAL_RECOGNITION_SLUG =
  "business-connecticut-facial-recognition-security";
export const IMPLEMENT_OPT_IN_CONSENT_BANNER_SLUG = "implement-opt-in-consent-banner";

export const physicalLocationQuestion = (): SurveyQuestionProps => {
  const slug = "business-physical-location";

  return {
    type: "boolean",
    slug: slug,
    question:
      "Does your business have a physical presence in California where it interacts directly with consumers, like a storefront?",
    helpText:
      "If you have a physical presence in California but you only collect personal data from employees (e.g., an office building) and do not collect data directly from your B2C (or B2B if applicable) customers at that physical location, answer “No.”",
    settingsQuestionLabel: "Has a physical presence in California",
  };
};

export const connecticutPhysicalLocationQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: CONNECTICUT_PHYSICAL_LOCATION_SLUG,
  question: "Does your business have a physical location in Connecticut?",
  settingsQuestionLabel: "Has a physical location in Connecticut",
});

export const connecticutFacialRecognitionQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: CONNECTICUT_FACIAL_RECOGNITION_SLUG,
  question:
    "Does your business use facial recognition technology at a physical location in Connecticut for the purpose of detecting or preventing security incidents, theft, fraud, or other illegal activity?",
  helpText:
    "For example, using security cameras to check the faces of customers as they enter a store to see if they match a database of known shoplifters.",
  settingsQuestionLabel: "Uses facial recognition for security at Connecticut locations",
});

export const businessKidsUnder18Question = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-18",
  question: "Does your business knowingly collect the personal data of minors under the age of 18?",
  helpText:
    'Answer "Yes" if your products, services, and/or website are aimed at minors under 18 or if you have actual knowledge that specific consumers are under 18 (for example, if your service systematically collects users\' birthdays).',
  settingsQuestionLabel: "Offers products/services aimed at minors under 18",
});

export const businessKidsUnder18DirectCollectionQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-18-direct-collection",
  question: "Does your business collect data directly from minors under 18?",
  helpText:
    'Answer "No" if you collect data about minors under 18 only from their parents or legal guardians. Answer "Yes" if you ask for consumers\' age (e.g., birthday with year) and continue collecting data from consumers even when they indicate that they are under 18 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
  settingsQuestionLabel: "Collects data directly from minors under 18",
});

export const businessKidsQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids",
  question:
    "Does your business knowingly collect the personal data of children under the age of 16?",
  helpText:
    "Answer “Yes” if your products, services, and/or website are aimed at kids under 16 or if you have actual knowledge that specific consumers are under 16 (for example, if your service systematically collects users’ birthdays).",
  settingsQuestionLabel: "Offers products/services aimed at kids under 16",
});

export const businessKidsDirectCollectionQuestion = (
  disabledAnswer?: "true" | "false",
): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-direct-collection",
  question: "Does your business collect data directly from children under 16?",
  helpText:
    'Answer “No” if you collect data about children under 16 only from their parents or legal guardians. Answer "Yes" if you ask for consumers\' age (e.g., birthday with year) and continue collecting data from consumers even when they indicate that they are under 16 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
  settingsQuestionLabel: "Collects data directly from children under 16",
  disabledAnswer,
});

export const KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG =
  "business-products-aimed-at-kids-under-13";
export const KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG = "business-products-aimed-at-kids";
export const businessKidsUnder13Question = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG,
  question: "Does your business knowingly collect personal data of children under the age of 13?",
  helpText:
    "Answer “Yes” if your business knowingly collects data from consumers under 13 years old.",
  settingsQuestionLabel: "Offers products/services aimed at kids under 13",
});

export const businessKidsUnder13DirectCollectionQuestion = (
  disabledAnswer?: "true" | "false",
): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-13-direct-collection",
  question: "Does your business collect data directly from children under 13?",
  helpText:
    'Answer “No” if you collect data about children under 13 only from their parents or legal guardians. Answer "Yes" if you collect consumers\' age (e.g., birthday with year) and collect data from consumers even when they indicate that they are under 13 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
  settingsQuestionLabel: "Collects data directly from children under 13",
  disabledAnswer,
});

export const businessKidsUnder13ParentalConsentQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-13-parental-consent",
  question:
    "When you collect the data of children who you know are under the age of 13, do you obtain parental consent to collect their data before doing so?",
  helpText:
    "Several states require parental consent before collecting data of minors under 13. If you do not currently do this, we'll create an item on your to-do list for you to implement consent requirements.",
  settingsQuestionLabel: "Parental consent for kids under 13",
});

export const businessMobileAppsQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-has-mobile-apps",
  question: "Does your business have a mobile application?",
  helpText:
    "Answer “yes” if you have an app that consumers can download from the Apple App Store or Google Play store.",
  settingsQuestionLabel: "Has a mobile application",
});

export const businessCaliforniaNonenglishQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-california-nonenglish",
  question:
    "Does your business regularly communicate with consumers in any languages other than English?",
  helpText:
    "Answer “yes” if your business regularly sends marketing emails to consumers in other languages, or if its contracts, terms or agreements are ordinarily provided in other languages.",
  settingsQuestionLabel: "Communicates with consumers in other languages",
});

export const businessTenMillionCaliforniansQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-california-10million",
  question:
    "Does your business have 10 million or more customers in California and/or get 10 million or more unique website visitors from California each year?",
  helpText:
    "The CCPA has enhanced reporting requirements for companies that handle data about a large portion of the population in California.",
});

export const MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG =
  "business-maryland-sensitive-data-compliance";
export const businessMarylandSensitiveDataComplianceQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG,
  question:
    'Maryland law prohibits your business from collecting this data unless it is strictly necessary to provide a product or service requested by the consumer whose data you collected. Maryland law also prohibits you from selling this data to others for monetary gain. Answer "yes" below if your business complies with these rules. If you answer "no," we\'ll create a task for you to complete that gets your business in compliance.',
  settingsQuestionLabel: "Maryland sensitive data compliance",
  onlyAnswerableFromDashboard: true,
});

export const businessServiceProviderQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-service-provider",
  question:
    "Does your business provide a service to other businesses (i.e., is it a “service provider”)?",
  settingsQuestionLabel: "Is a service provider",

  helpText: (
    <>
      Answer “no” if you sell products to consumers, either directly or through a marketplace. An
      example of a service provider is a B2B company like TrueVault or Salesforce.{" "}
      <ExternalLink
        href="https://knowledge.truevault.com/article/285-is-your-business-a-service-provider"
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline"
      >
        Read more about "service providers."
      </ExternalLink>
    </>
  ),
});

export const sellInExchangeForMoneyQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: "sell-in-exchange-for-money",
    question: "Does your business sell personal information to others in exchange for money?",
    helpText: <></>,
  };
};

export const businessCollectionForOtherBusinessesQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: COLLECTION_FOR_OTHER_BUSINESSES_SLUG,
  question:
    "Does your business collect (or purchase) personal information about individuals who it does not have a direct relationship with?",
  helpText: (
    <>
      For example, answer 'yes' if your business receives contact information about website visitors
      from a{" "}
      <ExternalLink
        href="https://www.truevault.com/learn/essentials/what-is-a-data-broker"
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline"
      >
        data broker
      </ExternalLink>{" "}
      like Retention.com or a tool like Wunderkind.
    </>
  ),
});

export const createProfilesAboutConsumersQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: "create-profiles-about-consumers",
    question: (
      <>
        <i>It’s exceedingly rare,</i> but does your business use automated profiling to make
        decisions having legal or similarly significant effects on consumers?
      </>
    ),
    helpText: (
      <>
        “Similarly significant effects” include decisions by your business to provide or deny a
        person money or credit, housing, insurance, education enrollment, criminal justice,
        employment opportunities, health care services, or access to basic necessities, such as food
        and water.
      </>
    ),
    settingsQuestionLabel: "Uses automated profiling with legal or similar effects",
  };
};

export const BUSINESS_LLM_TRAINING_QUESTION_SLUG = "llm-training-personal-data";
export const businessLlmTrainingQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: BUSINESS_LLM_TRAINING_QUESTION_SLUG,
  question:
    "Does your business collect, use, or sell personal data for the purpose of training a large language model (LLM)?",
  helpText:
    'For example, answer "yes" if your business has an AI chatbot on its website AND the contents of those chats are used to train an LLM.',
  settingsQuestionLabel: "Uses personal data to train an LLM",
});

export const NEW_JERSEY_SENSITIVE_DATA_SELLING_DISABLED_SLUG =
  "new-jersey-sensitive-data-selling-disabled";

// Returns each vendor marked as selling along with the names of the disclosed PICs that are
// considered sensitive in New Jersey. Mirrors the backend filtering in
// DataRecipientService.getDisclosedPICGroupsForSellingVendors (OrganizationDataRecipient.isSelling():
// vendors with ccpaIsSelling or always-selling vendors like SNI, Retention.com, and Wunderkind).
export const newJerseySensitiveSellingDisclosures = (
  vendors: OrganizationDataRecipientDto[],
  dataMap: DataMapDto,
): { vendorName: string; picNames: string[] }[] => {
  const sellingVendorsById = new Map(
    vendors
      .filter((v) => v.ccpaIsSelling || v.isAlwaysDataSellingVendor)
      .map((v) => [v.vendorId, v]),
  );

  const picNamesByVendorId = new Map<string, string[]>();
  for (const disclosure of dataMap.disclosure) {
    if (!sellingVendorsById.has(disclosure.vendorId)) continue;

    const pic = dataMap.categories[disclosure.picId];
    const isSensitiveInNj = pic?.sensitiveRegions?.some((r) => r.toLowerCase() === "nj");
    if (!isSensitiveInNj) continue;

    const picNames = picNamesByVendorId.get(disclosure.vendorId) || [];
    if (!picNames.includes(pic.name)) picNames.push(pic.name);
    picNamesByVendorId.set(disclosure.vendorId, picNames);
  }

  return [...picNamesByVendorId.entries()]
    .map(([vendorId, picNames]) => ({
      vendorName: sellingVendorsById.get(vendorId).name,
      picNames,
    }))
    .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
};

const NewJerseySensitiveSellingVendorList = ({
  vendors,
  dataMap,
}: {
  vendors: OrganizationDataRecipientDto[];
  dataMap: DataMapDto;
}) => {
  const disclosures = newJerseySensitiveSellingDisclosures(vendors, dataMap);

  if (disclosures.length === 0) {
    return (
      <p className="mt-2">
        <strong>
          According to your Data Map, you DO NOT have any vendors marked as "selling" sensitive
          personal information.
        </strong>
      </p>
    );
  }

  return (
    <div className="mt-2">
      <p className="mt-2">
        <strong>
          According to your Data Map, the following vendors are marked as "selling" these sensitive
          information categories:
        </strong>
      </p>
      <ul>
        {disclosures.map(({ vendorName, picNames }) => (
          <li key={vendorName}>
            <strong>{vendorName}</strong>: {picNames.join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const newJerseySensitiveDataSellingDisabledQuestion = (
  vendors?: OrganizationDataRecipientDto[],
  dataMap?: DataMapDto,
): SurveyQuestionProps => ({
  type: "boolean",
  slug: NEW_JERSEY_SENSITIVE_DATA_SELLING_DISABLED_SLUG,
  question: "Have you disabled all selling of consumers' sensitive data for New Jersey residents?",
  helpText: (
    <>
      New Jersey law now prohibits selling consumers' sensitive data. Any disclosure of sensitive
      data that is considered selling must be disabled in the state of New Jersey.
      {vendors && dataMap && (
        <NewJerseySensitiveSellingVendorList vendors={vendors} dataMap={dataMap} />
      )}
    </>
  ),
  yesLabel: "Confirm",
  yesAnswerLabel: "Confirmed",
  noLabel: "Remind Me",
  settingsQuestionLabel: "Disabled selling sensitive data in New Jersey",
  // Hidden until first answered from the dashboard, so orgs onboarding for the first time (who are
  // still building their data map) aren't asked prematurely. Once answered it becomes a permanent
  // part of the business survey.
  onlyAnswerableFromDashboard: true,
});

export const DISCLOSURE_FOR_SELLING_SLUG = "disclosure-for-selling";
export const sellingDisclosureQuestion = (
  sellingAndSharingAnswers?: SurveyAnswers,
  vendors?: OrganizationDataRecipientDto[],
): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: DISCLOSURE_FOR_SELLING_SLUG,
    question:
      "Does your business disclose — or give third parties access to — personal data about its consumers and receive free or discounted services, products, access to data, or anything else of value in return?",
    helpText: (
      <>
        This is considered “selling” under the broad definition in some state privacy laws and it
        requires that you give consumers the ability to opt-out. Select "Yes" if you disclose
        consumer data to marketing cooperatives and receive access to products, services, or special
        pricing in return, or if you disclose data in similar scenarios. Later, when you configure
        your vendors, we'll automatically select “Yes” if you use Google Analytics as a free service
        (and have not turned on RDP), or if you use certain email-scraping data brokers.
      </>
    ),
    noDisabled:
      sellingAndSharingAnswers && vendors?.length > 0
        ? overrideDisclosureForSellingQuestion(sellingAndSharingAnswers, vendors)
        : false,
    noDisabledTooltip: "'Yes' is selected here based on your answers to other questions.",
  };
};

const overrideDisclosureForSellingQuestion = (
  sellingAndSharingAnswers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): boolean => {
  const showGAQuestion = Boolean(findGoogleAnalytics(vendors));
  const showShopifyQuestion = Boolean(findShopifyAudiences(vendors));
  const hasAlwaysSellingVendors = findAlwaysSellingVendors(vendors).length > 0;

  const gaDisclosure =
    showGAQuestion && sellingAndSharingAnswers["google-analytics-data-sharing"] == "false";
  const shopifyDisclosure = showShopifyQuestion;

  return gaDisclosure || shopifyDisclosure || hasAlwaysSellingVendors;
};

export const SHARE_DATA_FOR_ADVERTISING_SLUG = "share-data-for-advertising";
export const shareDataQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: SHARE_DATA_FOR_ADVERTISING_SLUG,
  question: "Does your website use tracking technology from ad networks for targeted advertising?",
  helpText:
    "For example, if your website visitors see advertisements for products they viewed on your site after they leave your site and visit other sites.",
});

export const cipaOptInConsentBannerQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: IMPLEMENT_OPT_IN_CONSENT_BANNER_SLUG,
  header: "CIPA lawsuits against businesses have increased dramatically",
  question: "Would you like to implement an Opt-In Consent Banner?",
  helpText: (
    <React.Fragment>
      <p>
        Lawsuits and demand letters claiming violations of laws such as the{" "}
        <ExternalLink
          href="https://knowledge.truevault.com/article/342-protect-your-business-from-cipa-lawsuits"
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 underline"
        >
          California Invasion of Privacy Act
        </ExternalLink>{" "}
        (CIPA) and the Electronics Communications Privacy Act (ECPA) have become{" "}
        <strong>extremely common</strong>, with some businesses being forced to pay out large sums.
        According to plaintiffs, the use of technologies such as ad pixels and even website
        analytics without obtaining consent is equivalent to placing an illegal wiretap.
      </p>
      <p>
        While these lawsuits are not related to the privacy laws{" "}
        <ExternalLink
          href="https://www.truevault.com/privacy-laws-covered-by-truevault-products"
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 underline"
        >
          covered by our product
        </ExternalLink>
        , TrueVault does offer tools to help prevent being targeted by plaintiffs. When properly
        implemented, the <strong>Opt-In Consent Banner</strong> stops cookies, scripts, and other
        trackers from being deployed without consent. It can also be customized to only appear in
        certain states that present a higher litigation risk, such as California and Florida.
      </p>
    </React.Fragment>
  ),
  settingsQuestionLabel: "Wants to implement an Opt-In Consent Banner",
  onlyAnswerableFromDashboard: true,
});

export const USES_GOOGLE_TAG_MANAGER_SLUG = "uses-google-tag-manager";
export const usesGoogleTagManagerQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: USES_GOOGLE_TAG_MANAGER_SLUG,
  question: "Do you use Google Tag Manager (GTM) to load scripts on your website?",
  settingsQuestionLabel: "Uses Google Tag Manager (GTM)",
});

export const WEBSITE_HOSTING_PROVIDER_SLUG = "website-hosting-provider";
export const websiteHostingProviderQuestion = (): SurveyQuestionProps => ({
  type: "select",
  slug: WEBSITE_HOSTING_PROVIDER_SLUG,
  question: "Where is your website hosted?",
  settingsQuestionLabel: "Website hosting provider",
  placeholder: "Select a provider",
  otherPlaceholder: "Enter your website host",
  options: [
    { value: "shopify", label: "Shopify" },
    { value: "wordpress", label: "WordPress" },
    { value: "wix", label: "Wix" },
    { value: "squarespace", label: "Squarespace" },
    { value: "webflow", label: "Webflow" },
    { value: "godaddy", label: "GoDaddy" },
    { value: "bigcommerce", label: "BigCommerce" },
    { value: "adobe-commerce", label: "Adobe Commerce (Magento)" },
    { value: "salesforce-commerce-cloud", label: "Salesforce Commerce Cloud" },
    { value: "bentobox", label: "BentoBox" },
  ],
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const businessHipaaCompliantQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-hipaa-compliant",
  header: "Health Insurance Portability and Accountability Act (HIPAA)",
  question:
    "Is your business a “covered entity” or “business associate” that handles protected health information (PHI)?",
  helpText:
    "E.g., a healthcare provider, health plan, healthcare clearinghouse, or a business associate that processes PHI on behalf of another entity.",
  settingsQuestionLabel: "Collects data regulated by HIPAA",
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const businessGlbaCompliantQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-glba-compliant",
  header: "Gramm-Leach-Bliley Act (GLBA)",
  question:
    "Is your business a financial institution that provides a personal financial service or product?",
  helpText:
    "Eg., banks, securities brokers, insurance agents, finance companies, and even auto dealers if they extend credit, arrange financing, or give financial advice.",
  settingsQuestionLabel: "Collects data regulated by GLBA",
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const businessFcraCompliantQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-fcra-compliant",
  header: "Fair Credit Reporting Act (FCRA)",
  question:
    "Does your business share consumer personal information with consumer reporting agencies or receive said information from them?",
  helpText:
    "E.g., a property rental company provides data about evictions to a credit bureau; a car dealership pulls a credit report for a potential buyer.",
  settingsQuestionLabel: "Collects data regulated by FCRA",
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const BUSINESS_CONSUMER_HEALTH_QUESTION_SLUG = "business-identify-consumer-health";
export const BUSINESS_CONSUMER_HEALTH_CG_QUESTION_SLUG =
  "business-identify-consumer-health-collection-groups";
export const BUSINESS_CONSUMER_HEALTH_PIC_KEY = "protected-class-medical-condition";
export const businessConsumerHealthIdQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: BUSINESS_CONSUMER_HEALTH_QUESTION_SLUG,
  question:
    "Does your business use any consumer data it collects or stores to identify a consumer's physical or mental health condition or diagnosis?",
  helpText:
    "For example, answer “Yes” if your business uses browsing or purchase information to determine a consumer's potential fitness level, pregnancy status, fertility, risk of anxiety, stress, diabetes, or any other condition related to a consumer's physical or mental health.",
});

const businessQuestions = (
  answers: Record<string, string>,
  sellingAndSharingAnswers?: SurveyAnswers,
  vendors?: OrganizationDataRecipientDto[],
  dataMap?: DataMapDto,
  includeDashboardOnlyQuestions = false,
): SurveyQuestionProps[] => {
  // When adding or removing a question slug, also update SurveySlugs.BUSINESS_SURVEY_SLUGS on the backend
  return [
    physicalLocationQuestion(), // "business-physical-location"
    connecticutPhysicalLocationQuestion(), // business-connecticut-physical-location
    connecticutFacialRecognitionQuestion(), // business-connecticut-facial-recognition-security
    businessKidsUnder18Question(), // business-products-aimed-at-kids-under-18
    businessKidsUnder18DirectCollectionQuestion(), // business-products-aimed-at-kids-under-18-direct-collection
    businessKidsQuestion(), // business-products-aimed-at-kids
    businessKidsDirectCollectionQuestion(
      answers["business-products-aimed-at-kids-under-18-direct-collection"] == "false" &&
        answers["business-products-aimed-at-kids-direct-collection"] == "false"
        ? "false"
        : undefined,
    ), // business-products-aimed-at-kids-direct-collection
    businessKidsUnder13Question(), // business-products-aimed-at-kids-under-13
    businessKidsUnder13DirectCollectionQuestion(
      answers["business-products-aimed-at-kids-direct-collection"] == "false" &&
        answers["business-products-aimed-at-kids-under-13-direct-collection"] == "false"
        ? "false"
        : undefined,
    ), // business-products-aimed-at-kids-under-13-direct-collection
    businessKidsUnder13ParentalConsentQuestion(), // business-products-aimed-at-kids-under-13-parental-consent
    businessMobileAppsQuestion(), // business-has-mobile-apps
    businessCaliforniaNonenglishQuestion(), // business-california-nonenglish
    businessTenMillionCaliforniansQuestion(), // business-california-10million
    businessMarylandSensitiveDataComplianceQuestion(), // business-maryland-sensitive-data-compliance

    businessCollectionForOtherBusinessesQuestion(), // consumer-collection-for-other-businesses
    businessServiceProviderQuestion(), // business-service-provider
    sellInExchangeForMoneyQuestion(), // sell-in-exchange-for-money
    createProfilesAboutConsumersQuestion(), // create-profiles-about-consumers
    businessLlmTrainingQuestion(), // llm-training-personal-data
    usesGoogleTagManagerQuestion(), // uses-google-tag-manager
    websiteHostingProviderQuestion(), // website-hosting-provider

    sellingDisclosureQuestion(sellingAndSharingAnswers, vendors), // "disclosure-for-selling"
    newJerseySensitiveDataSellingDisabledQuestion(vendors, dataMap), // "new-jersey-sensitive-data-selling-disabled"
    shareDataQuestion(), // "share-data-for-advertising"
    ...(includeDashboardOnlyQuestions
      ? [cipaOptInConsentBannerQuestion()] // "implement-opt-in-consent-banner"
      : []),
  ];
};

const hasSellingSignal = (
  answers: Record<string, string>,
  sellingAndSharingAnswers?: SurveyAnswers,
  vendors?: OrganizationDataRecipientDto[],
): boolean => {
  return (
    answers[DISCLOSURE_FOR_SELLING_SLUG] == "true" ||
    answers["sell-in-exchange-for-money"] == "true" ||
    sellingAndSharingAnswers?.["selling-upload-data"] == "true" ||
    vendors?.some((vendor) => vendor.ccpaIsSelling || vendor.isUploadVendor) === true ||
    (sellingAndSharingAnswers && vendors
      ? overrideDisclosureForSellingQuestion(sellingAndSharingAnswers, vendors)
      : false)
  );
};

const businessVisibility = (
  answers: Record<string, string>,
  sellingAndSharingAnswers?: SurveyAnswers,
  vendors?: OrganizationDataRecipientDto[],
): Record<string, boolean> => {
  // When updating visibility, also update OrganizationOnboardingProgressService.countUnansweredBusinessQuestions()
  return {
    "business-physical-location": true,
    [CONNECTICUT_PHYSICAL_LOCATION_SLUG]: true,
    [CONNECTICUT_FACIAL_RECOGNITION_SLUG]: answers[CONNECTICUT_PHYSICAL_LOCATION_SLUG] == "true",
    "business-products-aimed-at-kids-under-18": true,
    "business-products-aimed-at-kids-under-18-direct-collection":
      answers["business-products-aimed-at-kids-under-18"] == "true",
    "business-products-aimed-at-kids":
      answers["business-products-aimed-at-kids-under-18"] == "true",
    "business-products-aimed-at-kids-direct-collection":
      answers["business-products-aimed-at-kids"] == "true",
    [KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG]:
      answers["business-products-aimed-at-kids"] == "true",
    "business-products-aimed-at-kids-under-13-direct-collection":
      answers["business-products-aimed-at-kids-under-13"] == "true",
    "business-products-aimed-at-kids-under-13-parental-consent":
      answers["business-products-aimed-at-kids-under-13"] == "true",
    "business-has-mobile-apps": true,
    "business-california-nonenglish": true,
    "business-california-10million": true,
    [COLLECTION_FOR_OTHER_BUSINESSES_SLUG]: true,
    "business-service-provider": true,
    "sell-in-exchange-for-money": true,
    "create-profiles-about-consumers": true,
    [BUSINESS_LLM_TRAINING_QUESTION_SLUG]: true,
    [USES_GOOGLE_TAG_MANAGER_SLUG]: true,
    [WEBSITE_HOSTING_PROVIDER_SLUG]: true,
    // Stays visible after "Remind Me" (a "false" answer) so the org can come back and answer "Yes"
    // once they've disabled selling of sensitive data (which also completes the associated task)
    [NEW_JERSEY_SENSITIVE_DATA_SELLING_DISABLED_SLUG]: hasSellingSignal(
      answers,
      sellingAndSharingAnswers,
      vendors,
    ),
    [IMPLEMENT_OPT_IN_CONSENT_BANNER_SLUG]: true,
    [MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG]: true,
  };
};

export const businessSurvey = (
  answers: SurveyAnswers,
  sellingAndSharingAnswers?: SurveyAnswers,
  vendors?: OrganizationDataRecipientDto[],
  dataMap?: DataMapDto,
  { includeDashboardOnlyQuestions = false }: { includeDashboardOnlyQuestions?: boolean } = {},
): SurveyDefinition => {
  // Determine which answers should be locked based on propagation across age brackets.
  const under13KnowinglyCollect = answers[KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG] == "true";
  const under16KnowinglyCollect = answers["business-products-aimed-at-kids"] == "true";
  const under13DirectCollection =
    answers["business-products-aimed-at-kids-under-13-direct-collection"] == "true";
  const under16DirectCollection =
    answers["business-products-aimed-at-kids-direct-collection"] == "true";
  const under18DirectCollectionIsNo =
    answers["business-products-aimed-at-kids-under-18-direct-collection"] == "false";
  const under16DirectCollectionIsNo =
    answers["business-products-aimed-at-kids-direct-collection"] == "false";
  const under13DirectCollectionIsNo =
    answers["business-products-aimed-at-kids-under-13-direct-collection"] == "false";

  // Build disabled reasons - if a string is provided, it acts as both the disabled flag AND the tooltip
  const disabled: Record<string, boolean | string> = {};

  // Lock under-16 "knowingly collect" to Yes if under-13 is Yes
  if (under13KnowinglyCollect) {
    disabled["business-products-aimed-at-kids"] =
      'This is locked to "Yes" because you indicated that you knowingly collect data from children under 13.';
  }

  // Lock under-18 "knowingly collect" to Yes if under-16 OR under-13 is Yes
  if (under13KnowinglyCollect) {
    disabled["business-products-aimed-at-kids-under-18"] =
      'This is locked to "Yes" because you indicated that you knowingly collect data from children under 13.';
  } else if (under16KnowinglyCollect) {
    disabled["business-products-aimed-at-kids-under-18"] =
      'This is locked to "Yes" because you indicated that you knowingly collect data from children under 16.';
  }

  // Lock under-16 "direct collection" to Yes if under-13 direct collection is Yes
  if (under13DirectCollection) {
    disabled["business-products-aimed-at-kids-direct-collection"] =
      'This is locked to "Yes" because you indicated that you collect data directly from children under 13.';
  } else if (under18DirectCollectionIsNo && under16DirectCollectionIsNo) {
    disabled["business-products-aimed-at-kids-direct-collection"] =
      'This is locked to "No" because you indicated that you do not collect data directly from minors under 18.';
  }

  // A broader No determines the corresponding narrower direct-collection answer.
  if (under16DirectCollectionIsNo && under13DirectCollectionIsNo) {
    disabled["business-products-aimed-at-kids-under-13-direct-collection"] =
      'This is locked to "No" because you indicated that you do not collect data directly from children under 16.';
  }

  // Lock under-18 "direct collection" to Yes if under-16 OR under-13 direct collection is Yes
  if (under13DirectCollection) {
    disabled["business-products-aimed-at-kids-under-18-direct-collection"] =
      'This is locked to "Yes" because you indicated that you collect data directly from children under 13.';
  } else if (under16DirectCollection) {
    disabled["business-products-aimed-at-kids-under-18-direct-collection"] =
      'This is locked to "Yes" because you indicated that you collect data directly from children under 16.';
  }

  return {
    questions: businessQuestions(
      answers,
      sellingAndSharingAnswers,
      vendors,
      dataMap,
      includeDashboardOnlyQuestions,
    ),
    visibility: businessVisibility(answers, sellingAndSharingAnswers, vendors),
    relatedQuestionGroups: [
      ["business-physical-location"],
      [CONNECTICUT_PHYSICAL_LOCATION_SLUG, CONNECTICUT_FACIAL_RECOGNITION_SLUG],
      [
        "business-products-aimed-at-kids-under-18",
        "business-products-aimed-at-kids-under-18-direct-collection",
        "business-products-aimed-at-kids",
        "business-products-aimed-at-kids-direct-collection",
        KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG,
        "business-products-aimed-at-kids-under-13-direct-collection",
        "business-products-aimed-at-kids-under-13-parental-consent",
      ],
      ["business-has-mobile-apps"],
      ["business-california-nonenglish"],
      ["business-california-10million"],
      [COLLECTION_FOR_OTHER_BUSINESSES_SLUG],
      ["business-service-provider"],
      ["sell-in-exchange-for-money"],
      ["create-profiles-about-consumers"],
      ["llm-training-personal-data"],
      [USES_GOOGLE_TAG_MANAGER_SLUG],
      [WEBSITE_HOSTING_PROVIDER_SLUG],
      [NEW_JERSEY_SENSITIVE_DATA_SELLING_DISABLED_SLUG],
      ...(includeDashboardOnlyQuestions ? [[IMPLEMENT_OPT_IN_CONSENT_BANNER_SLUG]] : []),
      [MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG],
    ],
    disabled,
  };
};
